// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * The signed-in app frame (Notion-like): a left sidebar with the account, the main places,
 * the vaults and workspaces, and sign-out; the page fills the rest. The vault workspace puts
 * its own files, search, and tags in the sidebar through `sidebar`.
 */
import { unwrap } from "@granite/api-client";
import { Avatar, cn, Icon, IconButton, type IconName, Menu, MenuContent, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger, type ThemePreference } from "@granite/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { createContext, type CSSProperties, type ReactNode, useContext, useEffect, useState } from "react";
import { useStore } from "zustand";

import { api } from "../lib/api.js";
import { t } from "../lib/i18n.js";
import { preferences, setPreference } from "../lib/preferences.js";
import { avatarSrc } from "../lib/avatar.js";
import { hasUser, logout, sessionStore } from "../lib/session.js";
import { sharedNotesQuery, sharedTitle } from "../lib/sharedNotes.js";
import { listVaults, type Vault } from "../lib/vaults.js";
import { Logo } from "./Logo.js";
import { NotificationsBell } from "./NotificationsBell.js";
import { rowClass } from "./sidebarRow.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { useSidebarWidth } from "./useSidebarWidth.js";
import { VaultMenu } from "./VaultMenu.js";
import { dragRegion } from "./WindowChrome.js";

export function AppShell({
  children,
  sidebar,
  sidebarOpen = true,
  onCollapse,
  footerAction,
  footerStart,
}: {
  children: ReactNode;
  /** Replaces the vault and workspace lists (the vault workspace's files, search, and tags). */
  sidebar?: ReactNode;
  sidebarOpen?: boolean;
  /** Shows a collapse button at the top of the sidebar (the vault workspace). */
  onCollapse?: () => void;
  /** The page's main action, at the bottom of the sidebar above Sign out (New note in a vault). */
  footerAction?: ReactNode;
  /** Takes Sign out's place at the bottom left (the vault switcher in a vault; Sign out stays in the account menu). */
  footerStart?: ReactNode;
}) {
  // Below 768px the sidebar is a slide-over, opened from the small bar at the top.
  const [mobileOpen, setMobileOpen] = useState(false);
  const path = useRouterState({ select: (st) => st.location.pathname });
  // The desktop app's settings open without an account: no account parts then.
  const signedIn = useStore(sessionStore, hasUser);
  useEffect(() => {
    setMobileOpen(false);
  }, [path]);
  const [sidebarWidth, resizeHandle] = useSidebarWidth();
  return (
    // Linear's frame: the sidebar sits on the darker frame, the page in a rounded panel beside it.
    // On the macOS desktop app the frame around the panel moves the window.
    <div {...dragRegion} className="flex h-dvh bg-sidebar text-fg">
      {mobileOpen && <div aria-hidden="true" className="fixed inset-0 z-30 bg-scrim md:hidden" onClick={() => { setMobileOpen(false); }} />}
      {(sidebarOpen || mobileOpen) && (
        <aside
          aria-label={t("shell.sidebar")}
          style={{ "--sidebar-w": `${sidebarWidth}px` } as CSSProperties}
          className={cn(
            "relative w-60 shrink-0 flex-col bg-sidebar md:w-(--sidebar-w)",
            mobileOpen ? "fixed inset-y-0 left-0 z-40 flex border-r border-border shadow-overlay md:static md:border-r-0 md:shadow-none" : "hidden md:flex",
          )}
        >
          {/* The window buttons sit here on the macOS desktop app. */}
          <div {...dragRegion} className="hidden h-10 shrink-0 titlebar:md:block" />
          <div className="flex items-center gap-1 pt-2 pr-2 pb-1.5 pl-2 titlebar:md:pt-0">
            <AccountMenu />
            {onCollapse && (
              <IconButton size="sm" label={t("shell.hideSidebar")} icon={<Icon name="panelLeft" />} className="hidden md:inline-flex" onClick={onCollapse} />
            )}
          </div>
          <nav aria-label={t("shell.mainNav")} className="flex flex-col gap-px px-2 pb-2">
            <NavLink to="/" icon="home" label={t("shell.home")} />
            {signedIn && <NotificationsBell />}
            <NavLink to="/settings" icon="settings" label={t("shell.settings")} />
            {signedIn && <AdminLink />}
          </nav>
          <div className="flex min-h-0 flex-1 flex-col">{sidebar ?? <Places />}</div>
          <SidebarFooter action={footerAction} start={footerStart} />
          {resizeHandle}
        </aside>
      )}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col overflow-hidden bg-bg md:my-2 md:mr-2 md:rounded-lg md:border md:border-border md:shadow-xs",
          !sidebarOpen && "md:ml-2",
        )}
      >
        <div {...dragRegion} className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-2 titlebar:pl-[78px] md:hidden">
          <IconButton size="sm" label={t("shell.openMenu")} icon={<Icon name="panelLeft" />} onClick={() => { setMobileOpen(true); }} />
          <Logo className="h-5" />
        </div>
        {children}
      </div>
    </div>
  );
}

/** Granite staff get a link to the admin console (GET /v1/me says who is staff). */
function AdminLink() {
  const me = useQuery({ queryKey: ["me"], queryFn: async () => unwrap(await api.GET("/v1/me")), staleTime: 5 * 60_000 });
  if (!me.data?.admin) return null;
  return <NavLink to="/admin" icon="users" label={t("shell.admin")} />;
}

function NavLink({ to, icon, label }: { to: "/" | "/settings" | "/admin"; icon: IconName; label: string }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const active = to === "/" ? path === "/" : path.startsWith(to);
  return (
    <Link to={to} search={{}} aria-current={active ? "page" : undefined} className={rowClass(active)}>
      <Icon name={icon} />
      {label}
    </Link>
  );
}

function AccountMenu() {
  const session = useStore(sessionStore);
  const navigate = useNavigate();
  const theme = useStore(preferences, (p) => p.theme);
  const signOut = useMutation({ mutationFn: logout });
  if (!hasUser(session)) return null;
  const { email, name, avatar_url } = session.user;
  const display = name.trim() || email.split("@")[0] || email;
  return (
    <div className="min-w-0 flex-1">
      <Menu>
        <MenuTrigger
          aria-label={t("shell.account", { email })}
          className="flex h-9 w-full items-center gap-2 rounded-md px-2 text-left outline-offset-[-2px] hover:bg-surface-muted/70"
        >
          <Avatar name={display} src={avatarSrc(avatar_url)} size={20} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-fg">{display}</span>
          </span>
          <Icon name="chevronDown" size={14} className="text-fg-muted" />
        </MenuTrigger>
        <MenuContent className="w-60">
          <MenuLabel className="truncate">{email}</MenuLabel>
          <MenuItem icon={<Icon name="settings" />} onSelect={() => void navigate({ to: "/settings", search: {} })}>
            {t("shell.settings")}
          </MenuItem>
          <MenuSeparator />
          <MenuLabel>{t("shell.theme")}</MenuLabel>
          <MenuRadioGroup
            value={theme}
            onValueChange={(v) => {
              setPreference("theme", v as ThemePreference);
            }}
          >
            <MenuRadioItem value="system">{t("settings.appearance.themeSystem")}</MenuRadioItem>
            <MenuRadioItem value="light">{t("settings.appearance.themeLight")}</MenuRadioItem>
            <MenuRadioItem value="dark">{t("settings.appearance.themeDark")}</MenuRadioItem>
          </MenuRadioGroup>
          <MenuSeparator />
          <MenuItem icon={<Icon name="logout" />} onSelect={() => { signOut.mutate(); }}>
            {t("shell.signOut")}
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}

/** Vaults grouped by who owns them: personal ones, then each workspace with its shared vaults. */
function Places() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const vaults = useQuery({ queryKey: ["vaults"], queryFn: listVaults });
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: async () => unwrap(await api.GET("/v1/workspaces")) });
  const items = vaults.data?.items ?? [];
  const vaultRow = (v: Vault) => (
    <li key={v.id}>
      <VaultMenu vault={v}>
        <Link to="/vault/$vaultId/$" params={{ vaultId: v.id }} search={{}} className={rowClass(path === `/vault/${v.id}` || path.startsWith(`/vault/${v.id}/`))}>
          <Icon name="vault" />
          <span className="truncate">{v.name}</span>
        </Link>
      </VaultMenu>
    </li>
  );
  const personal = items.filter((v) => !v.workspace_id);
  const shared = useQuery(sharedNotesQuery);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
      <SidebarHeading>{t("shell.personal")}</SidebarHeading>
      <ul className="flex flex-col gap-px">
        {personal.map(vaultRow)}
        {vaults.data && personal.length === 0 && <li className="px-2 py-1 text-xs text-fg-muted">{t("shell.noVaults")}</li>}
      </ul>
      {(shared.data?.length ?? 0) > 0 && (
        <>
          <SidebarHeading>{t("shell.sharedWithMe")}</SidebarHeading>
          <ul className="flex flex-col gap-px">
            {shared.data?.map((s) => (
              <li key={s.id}>
                <Link
                  to="/shared/$vaultId/$fileId"
                  params={{ vaultId: s.vault_id, fileId: s.file_id }}
                  title={s.owner_name || s.owner_email}
                  className={cn(rowClass(path === `/shared/${s.vault_id}/${s.file_id}`), !s.available && "opacity-60")}
                >
                  <Icon name="file" />
                  <span className="truncate">{sharedTitle(s.path)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      {workspaces.data?.items.map((w) => {
        const shared = items.filter((v) => v.workspace_id === w.id);
        return (
          <div key={w.id} className="mt-4">
            <div className="relative">
              <Link to="/w/$workspaceId" params={{ workspaceId: w.id }} className={cn(rowClass(path === `/w/${w.id}` || path.startsWith(`/w/${w.id}/`)), "pr-8 font-semibold")}>
                <Avatar name={w.name} size={16} />
                <span className="truncate">{w.name}</span>
              </Link>
              <WorkspaceAddMenu workspaceId={w.id} name={w.name} role={w.role} />
            </div>
            {shared.length > 0 && (
              <ul className="mt-px ml-3.5 flex flex-col gap-px border-l border-border pl-1.5">{shared.map(vaultRow)}</ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The + beside a workspace: add a member (admins) or a shared vault (members and up). */
export function WorkspaceAddMenu({ workspaceId, name, role }: { workspaceId: string; name: string; role: string }) {
  const navigate = useNavigate();
  const canInvite = role === "owner" || role === "admin";
  if (role === "guest") return null;
  const open = (section: "invitations" | "vaults") =>
    void navigate({ to: "/w/$workspaceId/$section", params: { workspaceId, section }, hash: "add" });
  return (
    <Menu>
      <MenuTrigger
        aria-label={t("shell.workspaceAdd", { name })}
        title={t("shell.workspaceAdd", { name })}
        className="absolute top-1/2 right-1 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-fg-muted hover:bg-surface-muted hover:text-fg data-[state=open]:bg-surface-muted data-[state=open]:text-fg"
      >
        <Icon name="plus" size={14} />
      </MenuTrigger>
      {/* The section opened puts focus in its field: the menu does not take it back to the +. */}
      <MenuContent align="start" className="w-48" onCloseAutoFocus={(e) => { e.preventDefault(); }}>
        {canInvite && (
          <MenuItem icon={<Icon name="user" />} onSelect={() => { open("invitations"); }}>
            {t("shell.addMember")}
          </MenuItem>
        )}
        <MenuItem icon={<Icon name="vault" />} onSelect={() => { open("vaults"); }}>
          {t("shell.addVault")}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function SidebarHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mt-4 mb-0.5 flex h-6 items-center justify-between px-2">
      <p className="text-xs font-medium text-fg-muted">{children}</p>
      {action}
    </div>
  );
}

function SidebarFooter({ action, start }: { action?: ReactNode; start?: ReactNode }) {
  const session = useStore(sessionStore);
  const signOut = useMutation({ mutationFn: logout });
  return (
    <div className="flex flex-col gap-2 px-2 py-2">
      {action}
      <div className="flex items-center gap-1">
        {start ? (
          <div className="min-w-0 flex-1">{start}</div>
        ) : (
          hasUser(session) && (
            <button type="button" className={cn(rowClass(false), "flex-1")} onClick={() => { signOut.mutate(); }}>
              <Icon name="logout" />
              {t("shell.signOut")}
            </button>
          )
        )}
        <a
          href={helpUrl}
          target="_blank"
          rel="noopener"
          aria-label={t("shell.helpTitle")}
          title={t("shell.helpTitle")}
          className="ml-auto inline-flex size-8 items-center justify-center rounded-md text-fg-muted hover:bg-surface-muted hover:text-fg"
        >
          <Icon name="help" />
        </a>
        <ThemeToggle />
      </div>
    </div>
  );
}

/** The help center on granite.md (opens in a new tab). */
const helpUrl = "https://granite.md/help";

/** Page title block: the title, a line of help, and actions on the right. */
export function PageHeader({ title, description, actions, children }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 pb-8">
      <div className="min-w-0">
        <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.015em] text-fg">{title}</h1>
        {description && <p className="mt-1.5 text-[13px] text-fg-muted">{description}</p>}
        {children}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}

/** A scrolling page body with the standard width and padding. */
export function Page({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <main className="min-h-0 flex-1 overflow-y-auto">
      <div className={cn("mx-auto px-5 pt-10 pb-24 md:px-10 md:pt-14", wide ? "max-w-5xl" : "max-w-[46rem]")}>{children}</div>
    </main>
  );
}

/** The open section's name on a page with a section menu: a Section titled the same hides its title. */
export const SectionPageTitle = createContext("");

/** A titled group of content, used on Home, Settings, and workspaces. */
export function Section({ title, description, actions, children, id }: { title: string; description?: string; actions?: ReactNode; children: ReactNode; id?: string }) {
  const titled = title !== useContext(SectionPageTitle);
  return (
    <section aria-labelledby={titled ? id : undefined} className="pb-10">
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          {titled && (
            <h2 id={id} className="text-[15px] font-semibold text-fg">
              {title}
            </h2>
          )}
          {description && <p className="mt-0.5 max-w-xl text-[13px] text-fg-muted">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Rows in one bordered box, separated by hairlines (Linear's settings and lists). */
export function Group({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <div role={label ? "group" : undefined} aria-label={label} className={cn("g-card divide-y divide-border overflow-hidden", className)}>
      {children}
    </div>
  );
}

/** One row of a Group: a label and help on the left, the control on the right. */
export function Row({ label, description, children, htmlFor }: { label: ReactNode; description?: ReactNode; children?: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex min-h-14 flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3">
      <div className="min-w-0 flex-1">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="block text-[13px] font-medium text-fg">
            {label}
          </label>
        ) : (
          <p className="text-[13px] font-medium text-fg">{label}</p>
        )}
        {description && <p className="mt-0.5 text-xs text-fg-muted">{description}</p>}
      </div>
      {children && <div className="flex shrink-0 items-center gap-2">{children}</div>}
    </div>
  );
}
